import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { GuaranteedMode } from '@nadoutong/contracts';
import {
  GUARANTEED_PROBE_CONFIRM_AFTER_SECONDS_DEFAULT,
  GUARANTEED_PROBE_QUALITY_MIN_SHOTS_DEFAULT,
  GUARANTEED_PROBE_QUALITY_MIN_BAD_SHOTS_DEFAULT,
  GUARANTEED_PROBE_QUALITY_RECOVER_PCT_DEFAULT,
  GUARANTEED_PROBE_QUALITY_RECOVER_WINDOW_MINUTES_DEFAULT,
  GUARANTEED_PROBE_QUALITY_TRIP_PCT_DEFAULT,
  GUARANTEED_PROBE_QUALITY_WINDOW_MINUTES_DEFAULT,
  GUARANTEED_PROBE_SLOW_MS_DEFAULT,
  GUARANTEED_PROBE_SHOT_SPACING_SECONDS_DEFAULT,
  GUARANTEED_PROBE_SHOT_TIMEOUT_SECONDS_DEFAULT,
  isGuaranteedJevMode,
} from '@nadoutong/contracts';
import { useT, type I18nKey } from '../../i18n/index.ts';
import { Chip, cx } from '../../ui/index.ts';
import { shortIdentifier } from '../tenantRoutingShared.ts';
import { LegRow } from './LegRow.tsx';
import { probeSummary } from './probeCopy.ts';
import { qualityBlindMinutesAfterReset } from './probeEffects.ts';
import { probeValuesOf } from './probeSettingsModel.ts';
import { LegShareBars } from './LegShareBars.tsx';
import type { LegSection } from './legRouting.ts';
import { overlayFallbackOnCount, type LegSku, type PlacedLeg } from './legSkus.ts';
import { routingChipLabeler, routingLabeler } from './legText.ts';
import { fallbackLabelKey, modeLabelKey, skuChainMode, skuFallbackMode, skuProbeMode } from './modes.ts';

const MODE_VALUE_TONE: Readonly<Record<GuaranteedMode, string>> = {
  act: 'bg-signal-dim text-signal',
  shadow: 'text-paper',
  off: 'text-paper-low',
};

function ModeChip({ name, mode, text }: {
  readonly name: string;
  readonly mode: GuaranteedMode;
  readonly text: string;
}): JSX.Element {
  return (
    <span className="inline-flex h-6 items-stretch overflow-hidden rounded border border-line text-label" data-mode={mode}>
      <span className="flex items-center bg-well px-2 text-paper-low">{name}</span>
      <span className={cx('flex items-center border-l border-line px-2', MODE_VALUE_TONE[mode])}>{text}</span>
    </span>
  );
}

/** A row from an older ops API may omit a counter; print a dash, never `undefined`. */
function fmtInt(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString('en-US') : '—';
}

/** 「deepseek/」 dimmed, the model bright: the namespace is already the rail box it sits in. */
function SkuTitle({ name }: { readonly name: string }): JSX.Element {
  const slash = name.indexOf('/');
  return (
    <span className="truncate font-mono text-title text-paper" title={name}>
      {slash < 0 ? name : (
        <>
          <span className="text-paper-low">{name.slice(0, slash + 1)}</span>
          <span>{name.slice(slash + 1)}</span>
        </>
      )}
    </span>
  );
}

function KeyValue({ label, children }: { readonly label: string; readonly children: ReactNode }): JSX.Element {
  return (
    <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">
      <span className="text-paper-low">{label}</span>
      <span className="text-paper-mid">{children}</span>
    </span>
  );
}

function Stat({ label, value, testId }: { readonly label: string; readonly value: string; readonly testId: string }): JSX.Element {
  return (
    <span className="flex flex-col items-end" data-testid={testId}>
      <span className="font-mono text-dense text-paper">{value}</span>
      <span className="text-micro text-paper-low">{label}</span>
    </span>
  );
}

function DetailHead({ sku }: { readonly sku: LegSku }): JSX.Element {
  const t = useT();
  const { row } = sku;
  const fallbackOn = overlayFallbackOnCount(sku);
  const jev: GuaranteedMode = row !== null && isGuaranteedJevMode(row.jev_judge) ? row.jev_judge : 'off';
  const shape = row !== null && typeof row.probe_shape === 'string' ? row.probe_shape : '—';
  return (
    <div className="flex flex-col gap-3 border-b border-line-soft px-4 pb-3.5 pt-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <SkuTitle name={sku.skuName} />
          <span className="font-mono text-micro text-paper-low" title={sku.skuId}>{shortIdentifier(sku.skuId)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {sku.firstRemoved === 0 ? null : (
            <span title={t('guaranteed.legs.first_removed_tip')}>
              <Chip tone="bad" lamp={false} testId={`guaranteed-leg-first-removed-${sku.skuId}`}>
                {t('guaranteed.legs.first_removed_badge', { count: String(sku.firstRemoved) })}
              </Chip>
            </span>
          )}
          {sku.takeovers === 0 ? null : (
            <span title={t('guaranteed.legs.takeover_tip')}>
              <Chip tone="warn" lamp={false} testId={`guaranteed-leg-takeover-${sku.skuId}`}>
                {t('guaranteed.legs.takeover_badge')}
              </Chip>
            </span>
          )}
          {sku.configured ? null : (
            <span title={t('guaranteed.leg.unconfigured_tip')}>
              <Chip tone="neutral" lamp={false} testId={`guaranteed-leg-sku-unconfigured-${sku.skuId}`}>
                {t('guaranteed.leg.unconfigured')}
              </Chip>
            </span>
          )}
          {row === null ? null : (
            <>
              <ModeChip name={t('guaranteed.legs.mode.probe')} mode={skuProbeMode(row)} text={t(modeLabelKey(skuProbeMode(row)))} />
              <ModeChip name={t('guaranteed.legs.mode.chain')} mode={skuChainMode(row)} text={t(modeLabelKey(skuChainMode(row)))} />
              <ModeChip name={t('guaranteed.legs.mode.jev')} mode={jev} text={t(modeLabelKey(jev))} />
              <ModeChip
                name={t('guaranteed.legs.mode.fallback')}
                mode={skuFallbackMode(row)}
                text={t(fallbackLabelKey(skuFallbackMode(row)))}
              />
            </>
          )}
          {sku.overlays.length === 0 ? null : (
            <ModeChip
              name={t('guaranteed.legs.mode.overlays')}
              mode={fallbackOn > 0 ? 'act' : 'off'}
              text={t('guaranteed.legs.mode.overlays_value', { count: String(sku.overlays.length), on: String(fallbackOn) })}
            />
          )}
        </div>
      </div>
      {row === null ? null : (
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-label" data-testid="guaranteed-leg-params">
            <KeyValue label={t('guaranteed.legs.kv.cadence')}>
              {t('guaranteed.legs.kv.cadence_value', {
                interval: fmtInt(row.probe_interval_minutes),
                shots: fmtInt(row.probe_shots_per_round),
                spacing: fmtInt(row.probe_shot_spacing_seconds ?? GUARANTEED_PROBE_SHOT_SPACING_SECONDS_DEFAULT),
                timeout: fmtInt(row.probe_shot_timeout_seconds ?? GUARANTEED_PROBE_SHOT_TIMEOUT_SECONDS_DEFAULT),
                shape,
              })}
            </KeyValue>
            <KeyValue label={t('guaranteed.legs.kv.trip')}>
              {t('guaranteed.legs.kv.trip_value', {
                n: fmtInt(row.probe_trip_failed_rounds),
                confirm: fmtInt(row.probe_confirm_after_seconds ?? GUARANTEED_PROBE_CONFIRM_AFTER_SECONDS_DEFAULT),
              })}
            </KeyValue>
            <KeyValue label={t('guaranteed.legs.kv.recover')}>
              {t('guaranteed.legs.kv.recover_value', { n: fmtInt(row.probe_recover_ok_rounds) })}
            </KeyValue>
            <KeyValue label={t('guaranteed.legs.kv.quality')}>
              {t('guaranteed.legs.kv.quality_value', {
                window: fmtInt(row.probe_quality_window_minutes ?? GUARANTEED_PROBE_QUALITY_WINDOW_MINUTES_DEFAULT),
                trip: fmtInt(row.probe_quality_trip_pct ?? GUARANTEED_PROBE_QUALITY_TRIP_PCT_DEFAULT),
                min: fmtInt(row.probe_quality_min_shots ?? GUARANTEED_PROBE_QUALITY_MIN_SHOTS_DEFAULT),
                recover: fmtInt(row.probe_quality_recover_pct ?? GUARANTEED_PROBE_QUALITY_RECOVER_PCT_DEFAULT),
                min_bad: fmtInt(row.probe_quality_min_bad_shots ?? GUARANTEED_PROBE_QUALITY_MIN_BAD_SHOTS_DEFAULT),
                recover_window: fmtInt(row.probe_quality_recover_window_minutes ?? GUARANTEED_PROBE_QUALITY_RECOVER_WINDOW_MINUTES_DEFAULT),
                slow: fmtInt(row.probe_slow_ms ?? GUARANTEED_PROBE_SLOW_MS_DEFAULT),
              })}
            </KeyValue>
            <KeyValue label={t('guaranteed.legs.kv.levels')}>
              <span className="tabular-nums" data-testid={`guaranteed-leg-probe-summary-${sku.skuId}`}>
                {probeSummary(probeValuesOf(row), t)}
              </span>
            </KeyValue>
          </div>
          <div className="flex gap-5">
            <Stat label={t('guaranteed.legs.stat.requests')} value={fmtInt(row.request_count_24h)} testId="guaranteed-leg-stat-requests" />
            <Stat label={t('guaranteed.legs.stat.entered')} value={fmtInt(row.entered_chain_count_24h)} testId="guaranteed-leg-stat-entered" />
            <Stat label={t('guaranteed.legs.stat.exhausted')} value={fmtInt(row.chain_exhausted_count_24h)} testId="guaranteed-leg-stat-exhausted" />
          </div>
        </div>
      )}
    </div>
  );
}

const SECTION_KEYS: Readonly<Record<LegSection, { title: I18nKey; hint: I18nKey }>> = {
  first: { title: 'guaranteed.legs.sec.first', hint: 'guaranteed.legs.sec.first_hint' },
  reserve: { title: 'guaranteed.legs.sec.reserve', hint: 'guaranteed.legs.sec.reserve_hint' },
  manual: { title: 'guaranteed.legs.sec.manual', hint: 'guaranteed.legs.sec.manual_hint' },
  overridden: { title: 'guaranteed.legs.sec.overridden', hint: 'guaranteed.legs.overridden_tip' },
  dead: { title: 'guaranteed.legs.sec.dead', hint: 'guaranteed.leg.tag.unreachable_tip' },
};

function SectionHead({ section, count, toggle }: {
  readonly section: LegSection;
  readonly count: number;
  /** Folded sections render their title as the toggle button. */
  readonly toggle?: { readonly open: boolean; readonly onToggle: () => void };
}): JSX.Element {
  const t = useT();
  const keys = SECTION_KEYS[section];
  const title = <span className="text-dense font-semibold text-paper">{t(keys.title)}</span>;
  return (
    <div className="flex flex-col gap-0.5 px-4 pb-2 pt-4" data-testid={section === 'dead' ? 'guaranteed-leg-unreachable-toggle-row' : undefined}>
      <div className="flex items-center gap-2">
        {toggle === undefined ? title : (
          <button
            type="button"
            aria-expanded={toggle.open}
            onClick={toggle.onToggle}
            className="inline-flex items-center gap-1 rounded-sm hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
            data-testid={section === 'dead' ? 'guaranteed-leg-unreachable-toggle' : `guaranteed-leg-section-toggle-${section}`}
          >
            {toggle.open
              ? <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 text-paper-low" />
              : <ChevronRight aria-hidden="true" className="h-3.5 w-3.5 text-paper-low" />}
            {title}
          </button>
        )}
        <span className="rounded-full bg-raised px-1.5 font-mono text-micro text-paper-mid">{count}</span>
      </div>
      <span className="text-micro text-paper-low">{t(keys.hint)}</span>
    </div>
  );
}

export function LegSkuDetail({ sku, nowMs }: { readonly sku: LegSku; readonly nowMs: number }): JSX.Element {
  const t = useT();
  const [deadOpen, setDeadOpen] = useState(false);
  const [overriddenOpen, setOverriddenOpen] = useState(false);
  const override = sku.routings.find((routing) => routing.kind === 'override');
  const label = routingLabeler(sku.routings, t);
  const chipLabel = routingChipLabeler(sku.routings, t);
  const bySection = (section: LegSection): readonly PlacedLeg[] => sku.legs.filter((placed) => placed.section === section);
  const dead = bySection('dead');
  const manual = bySection('manual');
  const overridden = bySection('overridden');
  const interval = sku.row?.probe_interval_minutes ?? null;
  const qualityBlindMinutes = qualityBlindMinutesAfterReset(sku.row);
  const rows = (legs: readonly PlacedLeg[]): JSX.Element => (
    <div className="mx-3 overflow-hidden rounded-lg border border-line-soft bg-card">
      {legs.map((placed) => (
        <LegRow
          key={placed.leg.key}
          placed={placed}
          arming={sku.arming}
          intervalMinutes={interval}
          qualityBlindMinutes={qualityBlindMinutes}
          label={label}
          chipLabel={chipLabel}
          nowMs={nowMs}
        />
      ))}
    </div>
  );
  return (
    <section
      className="min-w-0 flex-1 rounded-lg border border-line-soft bg-card pb-3"
      data-testid={`guaranteed-leg-detail-${sku.skuId}`}
    >
      <DetailHead sku={sku} />
      {override === undefined ? null : (
        <p
          className="m-0 border-b border-warn-line bg-warn-dim/35 px-4 py-2 text-label text-warn"
          data-testid="guaranteed-leg-override-notice"
        >
          {t('guaranteed.legs.override_notice', { name: override.configName ?? override.configId ?? '?' })}
        </p>
      )}
      {/* An unconfigured SKU lists only its removed legs: a split over those would be fiction. */}
      {sku.configured ? <LegShareBars routings={sku.routings} legs={sku.legs} /> : null}
      {(['first', 'reserve'] as const).map((section) => {
        const legs = bySection(section);
        return (
          <div key={section} data-testid={`guaranteed-leg-section-${section}`}>
            <SectionHead section={section} count={legs.length} />
            {legs.length === 0 ? (
              <p className="m-0 px-4 pb-1 text-label text-paper-low">{t('guaranteed.legs.sec.empty')}</p>
            ) : rows(legs)}
          </div>
        );
      })}
      {manual.length === 0 ? null : (
        <div data-testid="guaranteed-leg-section-manual">
          <SectionHead section="manual" count={manual.length} />
          {rows(manual)}
        </div>
      )}
      {overridden.length === 0 ? null : (
        <div data-testid="guaranteed-leg-section-overridden">
          <SectionHead
            section="overridden"
            count={overridden.length}
            toggle={{ open: overriddenOpen, onToggle: () => setOverriddenOpen((open) => !open) }}
          />
          {overriddenOpen ? rows(overridden) : null}
        </div>
      )}
      {dead.length === 0 ? null : (
        <div data-testid="guaranteed-leg-section-dead">
          <SectionHead
            section="dead"
            count={dead.length}
            toggle={{ open: deadOpen, onToggle: () => setDeadOpen((open) => !open) }}
          />
          {deadOpen ? rows(dead) : null}
        </div>
      )}
    </section>
  );
}
